/**
 * Group-active cart view. Renders the live `draftLines` from the store
 * (everyone at the table sees the same view via SSE) with attribution chips
 * + a Fire-to-kitchen CTA.
 *
 * This is a deliberate sibling of CartScreen rather than a branch in the
 * existing render — keeps the heavy promo/delivery logic out of the dine-in
 * group flow where it isn't relevant, and avoids accidentally shipping a
 * group line into the local-cart promotion engine.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Stepper, TopBar } from "@/components/ui";
import { AttributionChip } from "@/components/group/AttributionChip";
import { GroupBanner } from "@/components/group/GroupBanner";
import { GroupSheet } from "@/components/group/GroupSheet";
import { useGroupCart } from "@/store/group-cart";
import { useBranch } from "@/store/branch";
import { localize, useMenu } from "@/hooks/useMenu";

export function GroupCartView() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const groupCart = useGroupCart();
  const { branchConfig } = useBranch();
  const { items: menuItems } = useMenu();
  const [groupSheetOpen, setGroupSheetOpen] = useState(false);
  const [firing, setFiring] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [fireError, setFireError] = useState<string | null>(null);
  // Optimistic qty so the stepper feels instant — SSE round-trips reconcile.
  const [pendingQty, setPendingQty] = useState<Record<string, number>>({});

  const cart = groupCart.cart;
  const draftLines = cart?.draftLines ?? [];

  const itemById = useMemo(() => {
    const map = new Map<string, { name: string; imageUrl?: string | null }>();
    for (const it of menuItems) {
      map.set(it.id, { name: it.name, imageUrl: it.imageUrl });
    }
    return map;
  }, [menuItems]);

  const participantById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof cart>["participants"][number]>();
    for (const p of cart?.participants ?? []) map.set(p.guestId, p);
    return map;
  }, [cart]);

  if (!cart) {
    return null;
  }

  const handleFire = async () => {
    if (firing) return;
    setFiring(true);
    setFireError(null);
    try {
      // Pin from the menu-link branch config: routes the created order (and
      // its POS ordersQueue write) to the configured posInstance.
      const posId =
        branchConfig?.branches?.find((b) => b.branchId === cart.branchId)
          ?.posId ?? null;
      const r = await groupCart.fire(posId);
      navigate(`/dine-in-order/${r.orderId}`, { replace: true });
    } catch (err) {
      console.warn("[cart] fire failed", err);
      // Surface the failure so the user knows the button did something
      // instead of silently resetting the confirm sheet.
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message?: unknown }).message ?? "")
          : "";
      setFireError(
        msg ||
          t("v2.group_cart.fire_failed", {
            defaultValue:
              "Couldn't send the order to the kitchen. Try again in a moment.",
          }),
      );
      setFiring(false);
      setConfirming(false);
    }
  };

  return (
    <div style={{ paddingBottom: 180 }}>
      <TopBar
        title={t("v2.group_cart.cart_title", {
          count: cart.participants.length,
          defaultValue: `Group cart · ${cart.participants.length} people`,
        })}
        right={
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              color: "var(--primary-700)",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                background: "var(--primary-400, #34d399)",
                boxShadow: "0 0 0 4px rgba(52,211,153,0.18)",
              }}
            />
            {t("v2.group_cart.live")}
          </span>
        }
        onBack={() => navigate("/home")}
      />

      <GroupBanner
        participants={cart.participants}
        onTap={() => setGroupSheetOpen(true)}
      />

      <GroupSheet
        open={groupSheetOpen}
        onClose={() => setGroupSheetOpen(false)}
      />

      {draftLines.length === 0 ? (
        <div
          style={{
            padding: "60px 24px",
            textAlign: "center",
            color: "var(--ink-3)",
          }}
        >
          <div className="display"
            style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-1)" }}
          >
            {t("v2.group_cart.cart_empty_title")}
          </div>
          <div style={{ fontSize: 13, marginTop: 6 }}>
            {t("v2.group_cart.cart_empty_sub")}
          </div>
          <button
            onClick={() => navigate("/home")}
            style={{
              marginTop: 18,
              padding: "10px 18px",
              background: "var(--ink-1)",
              color: "#fff",
              borderRadius: "var(--radius-card)",
              border: "none",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <Icons.plus size={14} /> {t("v2.cart.add_more_items")}
          </button>
        </div>
      ) : (
        <>
          <div style={{ padding: "16px 16px 8px" }}>
            {draftLines.map((line) => {
              const meta = itemById.get(line.catalogItemId);
              const adder = participantById.get(line.addedByGuestId);
              const myGuestId = groupCart.participant?.guestId;
              const isMine = line.addedByGuestId === myGuestId;
              const displayQty = pendingQty[line.lineId] ?? line.quantity;

              // Build mods/variants label, mirroring DineInOrderScreen.
              const variantLabels = ((line.variantSelections ?? []) as Array<{
                variantOptionName?: { en?: string | null; ar?: string | null } | null;
              }>)
                .map((v) =>
                  v.variantOptionName
                    ? localize(v.variantOptionName, i18n.language)
                    : null,
                )
                .filter((s): s is string => !!s && s.length > 0);
              const modifierLabels = ((line.modifierSelections ?? []) as Array<{
                modifierOptionName?: { en?: string | null; ar?: string | null } | null;
                quantity?: number;
              }>)
                .map((m) => {
                  const label = m.modifierOptionName
                    ? localize(m.modifierOptionName, i18n.language)
                    : null;
                  if (!label) return null;
                  return m.quantity && m.quantity > 1
                    ? `${label} ×${m.quantity}`
                    : label;
                })
                .filter((s): s is string => !!s && s.length > 0);
              const modsLabel = [...variantLabels, ...modifierLabels];

              const onQty = (next: number) => {
                if (!isMine) return;
                if (next < 1) {
                  setPendingQty((m) => ({ ...m, [line.lineId]: 1 }));
                  void groupCart
                    .removeLine(line.lineId)
                    .catch((err) => {
                      console.warn("[cart] removeLine failed", err);
                      setPendingQty((m) => {
                        const { [line.lineId]: _omit, ...rest } = m;
                        return rest;
                      });
                    });
                  return;
                }
                setPendingQty((m) => ({ ...m, [line.lineId]: next }));
                void groupCart
                  .updateLine(line.lineId, { quantity: next })
                  .catch((err) => {
                    console.warn("[cart] updateLine failed", err);
                  })
                  .finally(() => {
                    // Drop the optimistic overlay — server state is now authoritative.
                    setPendingQty((m) => {
                      const { [line.lineId]: _omit, ...rest } = m;
                      return rest;
                    });
                  });
              };

              return (
                <div
                  key={line.lineId}
                  data-testid="group-line"
                  data-line-id={line.lineId}
                  data-item-id={line.catalogItemId}
                  data-name={meta?.name ?? ""}
                  data-qty={displayQty}
                  data-mine={isMine ? "1" : "0"}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "12px 0",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="display"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--ink-1)",
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {meta?.name ?? line.catalogItemId}
                      </span>
                      {!isMine && (
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--ink-3)",
                            fontWeight: 600,
                          }}
                        >
                          ×{line.quantity}
                        </span>
                      )}
                    </div>
                    {modsLabel.length > 0 && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--ink-2)",
                          marginTop: 4,
                          lineHeight: 1.4,
                        }}
                      >
                        {modsLabel.join(" · ")}
                      </div>
                    )}
                    {line.notes && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--ink-3)",
                          marginTop: 4,
                          fontStyle: "italic",
                        }}
                      >
                        "{line.notes}"
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      {isMine ? (
                        <>
                          <Stepper
                            value={displayQty}
                            onChange={onQty}
                            min={0}
                            size="sm"
                          />
                          <button
                            data-testid="group-line-remove"
                            onClick={() =>
                              void groupCart
                                .removeLine(line.lineId)
                                .catch((err) =>
                                  console.warn("[cart] removeLine failed", err),
                                )
                            }
                            style={{
                              background: "transparent",
                              border: 0,
                              color: "var(--ink-3)",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontSize: 12,
                              fontWeight: 600,
                              fontFamily: "inherit",
                              padding: 0,
                            }}
                          >
                            <Icons.trash size={13} />{" "}
                            {t("v2.cart.remove_line")}
                          </button>
                        </>
                      ) : null}
                      {adder && <AttributionChip participant={adder} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ padding: "0 16px 12px" }}>
            <button
              onClick={() => navigate("/home")}
              style={{
                width: "100%",
                height: 40,
                borderRadius: 10,
                background: "var(--surface-2)",
                border: "1px dashed var(--border-strong, #cbd5e1)",
                color: "var(--ink-2)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <Icons.plus size={14} /> {t("v2.cart.add_more_items")}
            </button>
          </div>

          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              // Sit above the global BottomNav (~58px) — matches the
              // CartScreen StickyBar offset.
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 66px)",
              padding: "10px 16px 12px",
              background: "var(--surface-1)",
              borderTop: "1px solid var(--border)",
              boxShadow: "0 -8px 24px -12px rgba(0,0,0,0.18)",
              zIndex: 20,
            }}
          >
            {fireError && (
              <div
                role="alert"
                style={{
                  fontSize: 12,
                  color: "#b91c1c",
                  background: "#fee2e2",
                  border: "1px solid #fecaca",
                  padding: "8px 12px",
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                {fireError}
              </div>
            )}
            {confirming ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--ink-2)",
                    textAlign: "center",
                  }}
                >
                  {t("v2.group_cart.fire_cta", {
                    count: draftLines.length,
                    defaultValue: `Fire ${draftLines.length} items to kitchen`,
                  })}
                  ?
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setConfirming(false)}
                    disabled={firing}
                    style={{
                      flex: 1,
                      height: 48,
                      borderRadius: 12,
                      background: "var(--surface-2)",
                      border: "1px solid var(--border)",
                      color: "var(--ink-1)",
                      fontSize: 14,
                      fontWeight: 700,
                    }}
                  >
                    {t("v2.common.cancel", "Cancel")}
                  </button>
                  <button
                    data-testid="group-fire-confirm"
                    onClick={handleFire}
                    disabled={firing}
                    style={{
                      flex: 2,
                      height: 48,
                      borderRadius: 12,
                      background: "#dc2626",
                      color: "#fff",
                      border: "none",
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: firing ? "wait" : "pointer",
                    }}
                  >
                    {firing
                      ? "…"
                      : t("v2.group_cart.fire_cta", {
                          count: draftLines.length,
                          defaultValue: `Fire ${draftLines.length} items to kitchen`,
                        })}
                  </button>
                </div>
              </div>
            ) : (
              <button
                data-testid="group-fire"
                onClick={() => setConfirming(true)}
                style={{
                  width: "100%",
                  height: 52,
                  borderRadius: 14,
                  background: "#dc2626",
                  color: "#fff",
                  border: "none",
                  fontSize: 16,
                  fontWeight: 800,
                  cursor: "pointer",
                  letterSpacing: 0.2,
                }}
              >
                {t("v2.group_cart.fire_cta", {
                  count: draftLines.length,
                  defaultValue: `Fire ${draftLines.length} items to kitchen`,
                })}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
